import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import useCanonical from "@/hooks/useCanonical";
import { scrollToSection } from "@/lib/scrollToSection";

// Above-the-fold — bundled in the initial chunk for instant paint.
import Nav from "@/components/relyn/Nav";
import Hero from "@/components/relyn/Hero";

// Below-the-fold — code-split. Each section is fetched only when the browser
// gets to it, cutting initial JS by roughly 60% on mobile.
const WhyRelyn = lazy(() => import("@/components/relyn/WhyRelyn"));
const WhoRelynHelps = lazy(() => import("@/components/relyn/WhoRelynHelps"));
const Features = lazy(() => import("@/components/relyn/Features"));
const HowItWorks = lazy(() => import("@/components/relyn/HowItWorks"));
const HowRelynHelps = lazy(() => import("@/components/relyn/HowRelynHelps"));
const Lifestyle = lazy(() => import("@/components/relyn/Lifestyle"));
const WhyMenChoose = lazy(() => import("@/components/relyn/WhyMenChoose"));
const AppPreview = lazy(() => import("@/components/relyn/AppPreview"));
const Marquee = lazy(() => import("@/components/relyn/Marquee"));
const WhoItsFor = lazy(() => import("@/components/relyn/WhoItsFor"));
const Benefits = lazy(() => import("@/components/relyn/Benefits"));
const Testimonials = lazy(() => import("@/components/relyn/Testimonials"));
const Differentiators = lazy(() => import("@/components/relyn/Differentiators"));
const Beta = lazy(() => import("@/components/relyn/Beta"));
const Security = lazy(() => import("@/components/relyn/Security"));
const Waitlist = lazy(() => import("@/components/relyn/Waitlist"));
const Press = lazy(() => import("@/components/relyn/Press"));
const DownloadCTA = lazy(() => import("@/components/relyn/DownloadCTA"));
const FAQ = lazy(() => import("@/components/relyn/FAQ"));
const Contact = lazy(() => import("@/components/relyn/Contact"));
const Footer = lazy(() => import("@/components/relyn/Footer"));
const InstallPrompt = lazy(() => import("@/components/relyn/InstallPrompt"));

// Skeleton block that reserves height so lazy loading never shifts the layout.
const SectionSkeleton = ({ h = 400 }) => (
  <div aria-hidden="true" style={{ minHeight: h }} />
);

export default function Landing() {
  useCanonical("/");
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let cancelled = false;
    const timers = [];

    const doScroll = () => {
      if (cancelled) return;
      const opts = id === "waitlist" ? { focus: "input" } : undefined;
      scrollToSection(id, opts);
    };

    // Retry across a few frames while lazy images + reveal blocks settle.
    [150, 500, 900, 1500].forEach((ms) => timers.push(setTimeout(doScroll, ms)));

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [location.hash, location.key]);

  return (
    <main className="relative min-h-screen overflow-hidden" data-testid="landing-page">
      <Nav />
      <Hero />
      <Suspense fallback={<SectionSkeleton />}>
        <WhyRelyn />
        <WhoRelynHelps />
        <Features />
        <HowItWorks />
        <HowRelynHelps />
        <Lifestyle />
        <WhyMenChoose />
        <AppPreview />
        <Marquee
          items={[
            "Self-Awareness",
            "Communication",
            "Reflection",
            "Confidence",
            "Growth",
            "Connection",
            "Intention",
          ]}
        />
        <WhoItsFor />
        <Benefits />
        <Testimonials />
        <Differentiators />
        <Beta />
        <Security />
        <Waitlist />
        <Press />
        <DownloadCTA />
        <FAQ />
        <Contact />
        <Footer />
        <InstallPrompt />
      </Suspense>
    </main>
  );
}
